export type EditorFlushHandle = {
  flush: () => Promise<void>;
  cancel: () => void;
  isBusy: () => boolean;
};

type OwnerBucket = {
  handles: Set<EditorFlushHandle>;
  /** Explicit dirty flag set by editors on first keystroke (before debounce). */
  dirty: boolean;
};

const owners = new Map<string, OwnerBucket>();
const listeners = new Set<() => void>();

function notifyListeners(): void {
  for (const listener of listeners) {
    listener();
  }
}

function getOrCreateBucket(ownerId: string): OwnerBucket {
  let bucket = owners.get(ownerId);
  if (!bucket) {
    bucket = { handles: new Set(), dirty: false };
    owners.set(ownerId, bucket);
  }
  return bucket;
}

function pruneBucket(ownerId: string): void {
  const bucket = owners.get(ownerId);
  if (!bucket) return;
  if (bucket.handles.size === 0 && !bucket.dirty) {
    owners.delete(ownerId);
  }
}

/**
 * Multiple editors may share an owner id (e.g. several paragraph clusters
 * under the same parent). Each registration is retained independently;
 * `flushEditor` / `flushAllEditors` flush every dirty/busy handle.
 */
export function registerEditorFlush(
  ownerId: string,
  handle: EditorFlushHandle,
): () => void {
  const bucket = getOrCreateBucket(ownerId);
  bucket.handles.add(handle);
  notifyListeners();
  return () => {
    const current = owners.get(ownerId);
    if (!current) return;
    current.handles.delete(handle);
    pruneBucket(ownerId);
    notifyListeners();
  };
}

/**
 * Editors call this on the first local change (and again after flush/cancel)
 * so the route can show dirty UI without waiting for the debounced Redux write.
 * Clearing is ignored while any sibling handle under the same owner is still busy.
 */
export function setEditorFlushDirty(ownerId: string, dirty: boolean): void {
  if (dirty) {
    const bucket = getOrCreateBucket(ownerId);
    if (bucket.dirty) return;
    bucket.dirty = true;
    notifyListeners();
    return;
  }
  const bucket = owners.get(ownerId);
  if (!bucket || !bucket.dirty) return;
  for (const handle of bucket.handles) {
    if (handle.isBusy()) return;
  }
  bucket.dirty = false;
  pruneBucket(ownerId);
  notifyListeners();
}

export function isEditorFlushDirty(ownerId?: string): boolean {
  if (ownerId !== undefined) {
    const bucket = owners.get(ownerId);
    if (!bucket) return false;
    if (bucket.dirty) return true;
    for (const handle of bucket.handles) {
      if (handle.isBusy()) return true;
    }
    return false;
  }
  for (const bucket of owners.values()) {
    if (bucket.dirty) return true;
    for (const handle of bucket.handles) {
      if (handle.isBusy()) return true;
    }
  }
  return false;
}

export function subscribeEditorFlushRegistry(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Snapshot for `useSyncExternalStore` — changes whenever dirty/busy set changes. */
export function getEditorFlushRegistrySnapshot(): string {
  const parts: string[] = [];
  for (const [ownerId, bucket] of owners) {
    let busy = false;
    for (const handle of bucket.handles) {
      if (handle.isBusy()) {
        busy = true;
        break;
      }
    }
    if (bucket.dirty || busy) {
      parts.push(`${ownerId}:${bucket.dirty ? 'd' : ''}${busy ? 'b' : ''}`);
    }
  }
  parts.sort();
  return parts.join('|');
}

export async function flushEditor(ownerId: string): Promise<void> {
  const bucket = owners.get(ownerId);
  if (!bucket) return;
  const current = [...bucket.handles].filter(handle => handle.isBusy());
  await Promise.all(current.map(handle => handle.flush()));
  if (bucket.dirty) {
    bucket.dirty = false;
    pruneBucket(ownerId);
    notifyListeners();
  }
}

export async function flushAllEditors(): Promise<void> {
  const entries = [...owners.entries()];
  await Promise.all(
    entries.flatMap(([ownerId, bucket]) =>
      [...bucket.handles]
        .filter(handle => handle.isBusy())
        .map(async handle => {
          await handle.flush();
          const after = owners.get(ownerId);
          if (after?.dirty) {
            after.dirty = false;
            pruneBucket(ownerId);
          }
        }),
    ),
  );
  notifyListeners();
}

export function cancelAllEditorFlushes(): void {
  for (const [ownerId, bucket] of [...owners.entries()]) {
    for (const handle of bucket.handles) {
      handle.cancel();
    }
    bucket.dirty = false;
    pruneBucket(ownerId);
  }
  notifyListeners();
}

export function cancelEditorFlush(ownerId: string): void {
  const bucket = owners.get(ownerId);
  if (!bucket) return;
  for (const handle of bucket.handles) {
    handle.cancel();
  }
  bucket.dirty = false;
  pruneBucket(ownerId);
  notifyListeners();
}

export function isEditorFlushBusy(ownerId: string): boolean {
  const bucket = owners.get(ownerId);
  if (!bucket) return false;
  if (bucket.dirty) return true;
  for (const handle of bucket.handles) {
    if (handle.isBusy()) return true;
  }
  return false;
}

/** Test helper — clears the registry between suites. */
export function __resetEditorFlushRegistryForTests(): void {
  owners.clear();
  listeners.clear();
}
